import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, Settings, Home as HomeIcon, Menu, X } from 'lucide-react';

/**
 * Layout Component
 * 
 * Main layout wrapper with navigation - styled to match main IDNTY website
 */
export function Layout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const location = useLocation();
  
  const navigation = [
    { name: 'Home', href: '/', icon: HomeIcon },
    { name: 'Configurations', href: '/configs', icon: Settings },
  ];
  
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header - matching main website style */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-white">
        <div className="container flex h-[100px] items-center justify-between max-w-[1400px] px-4">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/fNew_logo_2.png" 
              alt="IDNTY" 
              className="h-[80px] w-auto"
            />
            <div className="ml-4 flex flex-col">
              <span className="font-bold text-xl leading-tight">
                <span className="text-[#D4AF37]">IAM</span> Playground
              </span>
              <span className="text-xs text-muted-foreground uppercase tracking-widest">Identity & Access Management</span>
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-12">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-base font-medium transition-colors hover:text-[#D4AF37] ${
                    isActive ? 'text-[#D4AF37]' : 'text-[#333333]'
                  }`}
                >
                  <span>{item.name}</span>
                </Link>
              );
            })}
            <a
              href="https://idnty.one"
              target="_blank"
              rel="noopener noreferrer"
              className="text-base font-medium text-[#333333] hover:text-[#D4AF37] transition-colors"
            >
              Main Site →
            </a>
          </nav>
          
          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 hover:bg-muted rounded-md"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>
        
        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border">
            <nav className="container py-4 space-y-2">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium ${
                      isActive 
                        ? 'bg-primary/10 text-primary' 
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
              <a
                href="https://idnty.one"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted"
              >
                <span>idnty.one →</span>
              </a>
            </nav>
          </div>
        )}
      </header>
      
      {/* Main Content */}
      <main className="flex-1 container py-8">
        {children}
      </main>
      
      {/* Footer - matching main website style */}
      <footer className="border-t border-border bg-card">
        <div className="container py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-2">
              <Shield className="h-5 w-5 text-primary" />
              <span className="font-semibold">IDNTY</span>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              © {new Date().getFullYear()} IDNTY. IAM Playground for testing SAML & OIDC integrations.
            </p>
            <div className="flex items-center space-x-4 text-sm">
              <a 
                href="https://idnty.one" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                Main Site
              </a>
              <a 
                href="https://idnty.one/contact" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
